/**
 * ehr#437 (PROGRESS_NOTE_10-14-V81) / ehr#663: a progress note's session times and what is still
 * missing before it can be signed off or co-signed. Shared so the note editor, the session rosters
 * and the API (which refuses Co-sign & Finalize on an incomplete note) read one list.
 */
import { NO_CODE_KINDS, goalsSettingKind, isOtherKind, noteBlocks, withQuick, } from './noteLayouts.js';
import { FOCUS_LEFT_LABEL, addressedFocusOf, statusAddressedCount } from './noteFocus.js';
import { FORMAT_SECTIONS_V81 } from './noteSections.js';
import { noteTypeOf } from './noteKinds.js';
import { DISCHARGE_FINAL_STATUSES, REASON_STATUSES, noPlanReasonLeft } from './dischargeNote.js';
import { PEER_CREDENTIAL_LABEL, PEER_SUPPORT_KIND } from './peerSupport.js';
import { NO_SHOW_KIND } from './noShowNote.js';
/** Kinds of note that are therapy (Why treatment continues and Safety are asked on them). */
export const THERAPY_KINDS = ['Individual', 'Couples', 'Family', 'Group', 'Crisis'];
/** "9 AM" / "9:00am" / "14:30" -> "9:00 AM"; anything else -> ''. */
export function normalizeTime(t) {
    const m = /^\s*(\d{1,2})(?::(\d{2}))?\s*([ap])?\.?\s*m?\.?\s*$/i.exec(t);
    if (!m)
        return '';
    let h = Number(m[1]);
    const min = Number(m[2] ?? '0');
    if (min > 59)
        return '';
    const ap = m[3]?.toUpperCase();
    if (ap) {
        if (h < 1 || h > 12)
            return '';
    }
    else {
        if (h > 23)
            return '';
    }
    const pm = ap ? ap === 'P' : h >= 12;
    if (!ap)
        h = h % 12 || 12;
    return `${h}:${String(min).padStart(2, '0')} ${pm ? 'PM' : 'AM'}`;
}
/** ehr#266: "3:00 PM" -> "15:00" for the Credify time picker (TimeField); '' when unreadable. */
export function to24h(t) {
    const m = /^(\d{1,2}):(\d{2}) (AM|PM)$/.exec(normalizeTime(t));
    if (!m)
        return '';
    const h = (Number(m[1]) % 12) + (m[3] === 'PM' ? 12 : 0);
    return `${String(h).padStart(2, '0')}:${m[2]}`;
}
const toMin = (t) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(to24h(t));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
/**
 * Minutes from start to end ("3:00 PM" to "3:50 PM"), or null. An end up to 8 hours past midnight
 * counts as the next day (V81 sessionMinutes); otherwise an end before the start is negative.
 */
export function sessionMinutes(start, end) {
    const a = toMin(start);
    const b = toMin(end);
    if (a == null || b == null)
        return null;
    const m = b - a;
    return m < 0 && m + 1440 <= 480 ? m + 1440 : m;
}
/** A time plus some minutes, as the note stores times ("3:00 PM" + 50 = "3:50 PM"). */
export function addMinutes(start, minutes) {
    const a = toMin(start);
    if (a == null)
        return '';
    const n = (((a + minutes) % 1440) + 1440) % 1440;
    return normalizeTime(`${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`);
}
/** The length a note shows when its body has none (the editor's default). */
export const DEFAULT_SESSION_MINUTES = 50;
/**
 * ehr#666: a note's end time: the one saved, else its start plus its length (3:00 PM and 50 min is
 * 3:50 PM). Notes saved before the End time field (V81), the demo notes and notes a group session
 * started carry a start and a length but no end. '' when there is no start.
 */
export function sessionEndOf(s) {
    const end = typeof s?.end === 'string' ? s.end.trim() : '';
    if (end)
        return end;
    const time = typeof s?.time === 'string' ? s.time : '';
    if (!time.trim())
        return '';
    const len = typeof s?.durationMin === 'number' && s.durationMin > 0
        ? s.durationMin
        : DEFAULT_SESSION_MINUTES;
    return addMinutes(time, len);
}
/**
 * ehr#904: the note's date field is the date of service, on every kind of note (Individual,
 * Couples, Family, Group, Case management and the rest), and so is everything that names it.
 */
export const DATE_OF_SERVICE = 'Date of service';
/** ehr#858: the sign-off item for a session dated after today (ehr#904: named as the field is). */
export const NOT_FUTURE_LABEL = `${DATE_OF_SERVICE} no later than today`;
/**
 * ehr#858: a session dated after today can't be signed off (it hasn't happened yet). Today is
 * allowed, whatever the time; a draft may still be dated ahead. Both days are 'YYYY-MM-DD' (the
 * server's today is the practice's, in its time zone); anything else is not checked.
 */
export function futureDateLeft(date, today) {
    const day = /^\d{4}-\d{2}-\d{2}/;
    if (!date || !today || !day.test(date) || !day.test(today))
        return [];
    return date.slice(0, 10) > today.slice(0, 10)
        ? [{ label: NOT_FUTURE_LABEL, target: 'session', future: true }]
        : [];
}
/**
 * ehr#853: a Credify Note's own text boxes, Objective and Assessment (FORMAT_SECTIONS.CREDIFY),
 * are required before sign-off, as SOAP, DAP and BIRP require theirs. They sit in the template's
 * own fields (CredifyNoteFields), not in the layout's blocks, so they are checked here. The Other
 * notes never use the Credify template (they are written on the SOAP base).
 */
export function credifyTextLeft(format, kind, text) {
    if (format !== 'CREDIFY' || isOtherKind(kind))
        return [];
    return (FORMAT_SECTIONS_V81.CREDIFY ?? [])
        .filter(([, key]) => !(text[key] ?? '').trim())
        .map(([label, key]) => ({ label, target: `sec-${key}`, narrative: true }));
}
/** ehr#856: the sign-off item for an End time at or before the Start time. */
export const END_AFTER_START_LABEL = 'End time after the start time';
/**
 * ehr#727 (PEER_SUPPORT_NOTE_10-30-V8, 13a): a Peer support note is signed with the signer's peer
 * specialist credential; with none it can't be signed, and the item can't be skipped.
 */
export function peerCredentialLeft(kind, credential) {
    if (kind !== PEER_SUPPORT_KIND || credential === undefined || credential.trim())
        return [];
    return [{ label: PEER_CREDENTIAL_LABEL, target: 'peer-credential', credential: true }];
}
/**
 * ehr#682 (V141 V133, V139): "At least one problem, goal, or objective addressed", when the
 * practice asks for it, on a progress note that shows the section; never on a no-show or an
 * admin / phone note (nothing was addressed). The Other notes (Service notes, Discharge summary,
 * Treatment plan review, ...) show the section but don't require it: V141 is the progress note.
 * ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9 22 A): a case management note shows the section only
 * while the practice's Show treatment plan goals is on (`planGoals`), and then its rule applies.
 */
export function focusLeft(o) {
    if (!o.require || o.picked > 0)
        return [];
    if (NO_CODE_KINDS.includes(o.kind))
        return [];
    if (isOtherKind(o.kind) && !(o.planGoals && goalsSettingKind(o.kind)))
        return [];
    return [{ label: FOCUS_LEFT_LABEL, target: 'focus', focus: true }];
}
/**
 * ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V5): the kinds of note that can't be signed (or co-signed)
 * while a linked treatment plan goal or objective has no final status.
 */
export const GOAL_FINAL_KINDS = ['Discharge summary'];
/**
 * ehr#708 (Jay 10-30, Q1): the statuses that count as final on those kinds: Met, Partially met, Not
 * met and Discontinued. In progress, Not started and Not addressed this session block sign-off.
 */
export const FINAL_GOAL_STATUSES = DISCHARGE_FINAL_STATUSES;
/**
 * ehr#708: the note's status rows in the order the goals section shows them (V141): a goal with
 * objectives is its own row (body.goalStatuses) then one per objective; a goal with no objective
 * is one row. From the saved goalsAddressed and goalStatuses, as the editor groups them.
 */
export function goalStatusRows(goalsAddressed, goalStatuses) {
    const rows = (Array.isArray(goalsAddressed) ? goalsAddressed : []).filter((r) => !!r && typeof r === 'object');
    const levels = (Array.isArray(goalStatuses) ? goalStatuses : []).filter((r) => !!r && typeof r === 'object');
    const groups = [];
    for (const r of rows) {
        const g = str(r.goalText);
        const grp = groups.find((x) => x.goalText === g);
        if (grp)
            grp.rows.push(r);
        else
            groups.push({ goalText: g, rows: [r] });
    }
    const st = (v) => (typeof v === 'string' && v ? v : undefined);
    // ehr#708: the status note rides along only when there is one (a Discontinued row's reason)
    const row = (text, r) => {
        const note = typeof r?.note === 'string' && r.note.trim() ? r.note : undefined;
        return { text, status: st(r?.status), ...(note ? { note } : {}) };
    };
    return groups.flatMap((grp) => grp.rows.length === 1 && !str(grp.rows[0].objectiveText)
        ? [row(grp.goalText || 'the goal', grp.rows[0])]
        : [
            row(grp.goalText || 'the goal', levels.find((l) => str(l.goalText) === grp.goalText)),
            ...grp.rows.map((r) => row(str(r.objectiveText), r)),
        ]);
}
/**
 * ehr#708: the "Status for <goal/objective>" items still needed on a GOAL_FINAL_KINDS note (no
 * status, or one that isn't final), and "Reason for discontinuing <...>" for a Discontinued row
 * with no reason. Jay 10-30 Q3: only while the practice's "Goal status required at sign-off" is on
 * (`require`, its default).
 */
export function goalStatusLeft(kind, rows, require = true) {
    if (!require || !GOAL_FINAL_KINDS.includes(kind))
        return [];
    return rows.flatMap((r) => {
        if (!r.status || !FINAL_GOAL_STATUSES.includes(r.status))
            return [{ label: `Status for ${r.text}`, target: 'goals', goal: true }];
        if (REASON_STATUSES.includes(r.status) && !r.note?.trim())
            return [{ label: `Reason for discontinuing ${r.text}`, target: 'goals', goal: true }];
        return [];
    });
}
/**
 * ehr#713 (NO_SHOW_CANCELLATION_NOTE_10-29-V3, the 10-09 rule): the kinds of note a phrase blank
 * (__) left in a box keeps from being signed. No-show only: Jay, 10-30 on #713 Q2, "just do it
 * for this note right now" (widen by adding the kinds here).
 */
export const BLANK_CHECK_KINDS = ['No-show / cancellation'];
/** Two or more underscores in a row: a phrase pill's fill-in blank ("Rescheduled for __."). */
export const hasBlank = (t) => /_{2,}/.test(t);
/**
 * What still has to be filled in before the note can be signed off, in note order (V81
 * signoffMissingItems: "7 left to sign: Subjective, Objective, ..."). The parts come from the
 * note's layout (noteLayouts). Safety itself may stay Not assessed; Concerns reported needs
 * all four risk answers. A no-show has no end time. The server checks the diagnosis too.
 */
export function leftToSign(n) {
    const out = [];
    const add = (label, target) => out.push({ label, target });
    const therapy = THERAPY_KINDS.includes(n.kind);
    const blanks = BLANK_CHECK_KINDS.includes(n.kind);
    if (!n.start.trim())
        add('Start time', 'session');
    // A no-show needs no End time, but one it is given can't be at or before the Start (ehr#856:
    // a no-show signed and locked at 11:30 PM to 10:00 PM)
    const noShow = n.kind === 'No-show / cancellation';
    if (!noShow && !n.end.trim())
        add('End time', 'session');
    else if (n.start.trim() && n.end.trim()) {
        const m = sessionMinutes(n.start, n.end);
        if (m !== null && m <= 0)
            out.push({ label: END_AFTER_START_LABEL, target: 'session', time: true });
    }
    out.push(...futureDateLeft(n.date, n.today)); // ehr#858
    // Why treatment continues sits under Assessment (else Plan) on therapy notes.
    const host = n.blocks.some((b) => b.id === 'assessment') ? 'assessment' : 'plan';
    for (const b of n.blocks) {
        if (b.kind === 'dx' && b.required && n.diagnoses === 0)
            add('Diagnosis', 'dx');
        // ehr#708: a discharge summary gives every goal and objective a final status (no plan: none)
        if (b.kind === 'goals') {
            out.push(...goalStatusLeft(n.kind, n.goalRows ?? [], n.requireGoalStatus ?? true));
            // ehr#708 (Q2): no plan to give statuses to: the reason why instead
            if (n.noPlan)
                out.push(...noPlanReasonLeft({ kind: n.kind, goalRows: n.goalRows?.length ?? 0, ...n.noPlan }));
        }
        if (b.kind === 'focus' && n.focus)
            out.push(...focusLeft({ ...n.focus, kind: n.kind }));
        if (b.kind === 'interventions' && b.required && !n.interventionsUsed.length)
            add(b.label, 'interventions');
        if (b.kind === 'text') {
            if (b.required && !(n.text[b.id] ?? '').trim())
                // ehr#731: a no-show's Contact note (required on its layout) is flagged for the server
                out.push({
                    label: b.label,
                    target: `sec-${b.id}`,
                    ...(n.kind === NO_SHOW_KIND && b.id === 'subjective' ? { contact: true } : {}),
                });
            if (blanks && hasBlank(n.text[b.id] ?? ''))
                out.push({
                    label: `Fill in the blank (__) in ${b.label}`,
                    target: `sec-${b.id}`,
                    blank: true,
                });
            if (b.id === host && therapy && !n.necessity.trim())
                add('Why treatment continues', 'necessity');
        }
        if (b.kind === 'safety' && n.safety?.status === 'Concerns reported') {
            const s = n.safety;
            if (!s.level)
                add('Risk level', 'safety');
            if (!s.actions?.length)
                add('Actions taken', 'safety');
            if (!s.notified?.trim())
                add('Who was notified', 'safety');
            if (!s.followup?.trim())
                add('Follow-up plan', 'safety');
        }
    }
    // ehr#853: a Credify Note's own boxes follow the layout's parts, as the editor shows them
    out.push(...credifyTextLeft(n.format, n.kind, n.text));
    // ehr#727: the credential sits under the signature, after the note's parts
    out.push(...peerCredentialLeft(n.kind, n.peerCredential));
    return out;
}
/** An absent group or family member's note is written on the no-show layout (GROUP_NOTE V22 26 B). */
export const ABSENT_KIND = 'No-show / cancellation';
const str = (v) => (typeof v === 'string' ? v : '');
const strs = (v) => Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
/**
 * A saved note's kind as noteLeftToSign reads it: the body's own (body.noteType), else its session
 * type's. ehr#727: the server reads it to sign a Peer support note with its author's credential.
 */
export function savedNoteKind(body, isGroup) {
    const type = (body.session ?? {}).type;
    return typeof body.noteType === 'string' && body.noteType
        ? body.noteType
        : noteTypeOf({ sessionLabel: str(type) || 'Individual', isGroup: !!isGroup });
}
/**
 * ehr#663: what a saved note's body still needs, read the way the editor reads it: its kind (the
 * body's own, else its session type's), its template (Other notes on the SOAP base), its layout
 * (an absent group or family member's is the no-show one), its text boxes, its times (the end
 * worked out from the start and length when none was saved) and its clinical parts. Note order.
 */
export function noteLeftToSign(body, o) {
    const session = (body.session ?? {});
    const noteKind = savedNoteKind(body, o.isGroup);
    const kind = o.absentMember ? ABSENT_KIND : noteKind;
    const format = isOtherKind(noteKind) ? 'SOAP' : o.format;
    const quick = Array.isArray(body.quick) ? body.quick : undefined;
    const blocks = withQuick(noteBlocks(format, kind, { planGoals: o.planGoals, absent: o.absentMember }), quick, format);
    const text = {};
    for (const b of blocks)
        if (b.kind === 'text')
            text[b.id] = str(body[b.id]);
    // ehr#853: a Credify Note's Objective and Assessment (its own fields, not layout blocks)
    if (format === 'CREDIFY')
        for (const [, key] of FORMAT_SECTIONS_V81.CREDIFY ?? [])
            text[key] = str(body[key]);
    return leftToSign({
        blocks,
        kind,
        text,
        interventionsUsed: strs(body.interventionsUsed),
        necessity: str(body.necessity),
        safety: body.safety ?? null,
        diagnoses: Array.isArray(body.diagnoses) ? body.diagnoses.length : 0,
        start: str(session.time),
        end: sessionEndOf(session),
        goalRows: goalStatusRows(body.goalsAddressed, body.goalStatuses),
        requireGoalStatus: o.requireGoalStatus,
        noPlan: { linkedPlanId: body.linkedPlanId, reason: body.noPlanReason },
        ...(o.requireFocus !== undefined
            ? {
                focus: {
                    require: o.requireFocus && !o.isGroup && !o.absentMember,
                    // ehr#908: a goal or objective given a status (not "Not addressed this session")
                    // in Treatment plan goals counts as addressed too
                    picked: addressedFocusOf(body).length +
                        statusAddressedCount(goalStatusRows(body.goalsAddressed, body.goalStatuses)),
                    planGoals: !!o.planGoals,
                },
            }
            : {}),
        // ehr#727: the credential the note was saved with (body.peerCredential)
        peerCredential: str(body.peerCredential),
        date: o.date, // ehr#858
        today: o.today,
        format, // ehr#853
    });
}
//# sourceMappingURL=noteSignChecks.js.map